import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  Lock,
  CreditCard,
  Boxes,
  Calendar,
  Layers,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison rapide ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison rapide
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison rapide de cartons de déménagement à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Gagnez du temps dans l&apos;organisation de votre déménagement
            grâce à notre service de livraison de cartons à {city.name}.
            Plus besoin de vous déplacer pour récupérer vos fournitures :
            nous acheminons directement votre commande à l&apos;adresse de
            votre choix.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Que vous ayez besoin de quelques cartons ou d&apos;un kit
            complet de déménagement, notre équipe assure une livraison
            rapide et soignée afin que vous puissiez débuter vos
            préparatifs sans attendre. Profitez d&apos;un service pratique,
            flexible et adapté à vos contraintes pour recevoir tout le
            matériel nécessaire dans les meilleures conditions.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Service rapide",
              body: "Préparation soignée et livraison sous 24-72 h selon disponibilité.",
              Icon: Truck,
            },
            {
              title: "Volumes flexibles",
              body: "De quelques cartons à un kit complet pour grande maison, nous adaptons l'envoi.",
              Icon: Boxes,
            },
            {
              title: "Créneau au choix",
              body: "Fixez la plage horaire qui vous arrange et nous respectons votre planning.",
              Icon: Calendar,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Comment passer votre commande ? (parcours horizontal) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Parcours d&apos;achat
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Comment passer votre commande en ligne&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Commander vos fournitures de déménagement n&apos;a jamais été
            aussi simple et rapide.
          </p>
        </div>

        {/* Ligne de parcours horizontale */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-0 relative">
          <div className="hidden md:block absolute top-7 left-[16.7%] right-[16.7%] h-px bg-gradient-to-r from-[#237cb5]/30 via-[#237cb5] to-[#237cb5]/30" />
          {[
            {
              n: "01",
              title: "Sélectionnez vos produits",
              body: "Parcourez notre boutique et choisissez vos cartons à l'unité ou optez pour l'un de nos packs complets adaptés à la taille de votre logement.",
              Icon: Boxes,
            },
            {
              n: "02",
              title: "Validez votre panier",
              body: `Ajoutez le matériel de protection nécessaire (film bulle, adhésifs) et indiquez votre adresse de livraison à ${city.name}.`,
              Icon: Layers,
            },
            {
              n: "03",
              title: "Planifiez la réception",
              body: "Finalisez votre commande et notre service logistique interne prend le relais pour planifier la livraison directement chez vous selon vos disponibilités.",
              Icon: Calendar,
            },
          ].map(({ n, title, body, Icon }) => (
            <div key={n} className="relative md:px-4">
              <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-[#237cb5] text-white shadow-[0_8px_24px_-8px_rgba(35,124,181,0.6)] relative z-10">
                <Icon className="h-6 w-6" strokeWidth={2.4} />
              </div>
              <div className="mt-1.5 text-center text-[11px] uppercase tracking-[0.2em] font-bold text-[#237cb5]">
                Étape {n}
              </div>
              <h3 className="mt-3 text-center font-display font-extrabold text-[16px] text-[#0f1d2b]">
                {title}
              </h3>
              <p className="mt-2 text-center text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-bois-colombes-cta-shop"
          >
            Commencer ma commande
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-bois-colombes-cta-packs"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Voir les kits complets
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Paiement sécurisé (version sobre) ======= */}
    <section className="py-14 md:py-20 bg-[#0f1d2b] text-white">
      <div className="container-cdm">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] uppercase tracking-[0.18em] font-bold text-white/80 mb-4">
            <Lock className="h-3.5 w-3.5" strokeWidth={2.6} />
            Sécurité bancaire
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px]">
            Un paiement 100&nbsp;% sécurisé pour vos achats
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-white/80 leading-relaxed">
            Achetez vos fournitures en toute sérénité sur notre boutique en
            ligne. Nous utilisons des protocoles de sécurité de dernière
            génération (cryptage SSL) pour garantir la totale
            confidentialité de vos coordonnées bancaires lors de la
            transaction. Que vous choisissiez de régler par carte bancaire
            ou via nos solutions partenaires, votre paiement est
            entièrement sécurisé, vous permettant de valider vos achats en
            toute confiance et sans le moindre risque.
          </p>

          <div className="mt-8 inline-flex items-center gap-3 md:gap-4 flex-wrap justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
              <Lock className="h-4 w-4 text-[#FF7A00]" strokeWidth={2.6} />
              <span className="text-[13px] font-bold">Cryptage SSL</span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
              <CreditCard className="h-4 w-4 text-[#FF7A00]" strokeWidth={2.6} />
              <span className="text-[13px] font-bold">CB & partenaires</span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
              <ShieldCheck className="h-4 w-4 text-[#FF7A00]" strokeWidth={2.6} />
              <span className="text-[13px] font-bold">0 risque</span>
            </div>
          </div>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-bois-colombes-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
